import React, { useCallback, useContext, useMemo } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import PropTypes from 'prop-types';
import classnames from 'clsx';

import { emptyArray } from 'ui-components/default-props/empty';
import { useContextTypes } from '../../../hooks/use-context-types';
import { reorderGridColumns } from '../../../services/grid-column-helper-service';
import { DisplayGridColumnPropType } from '../../../prop-types/display-grid-column';
import { DisplayGridRecordPropType } from '../../../prop-types/display-grid-record';
import { DisplayGridGroupRecordPropType } from '../../../prop-types/display-grid-group-record';
import { GridHelperService } from '../../../services/grid-helper-service';
import { GuidService } from '../../../services/guid-service';

import { setColumns, setIsResizing, setResetToScrollTop } from '../actions';
import { Context } from '../context';
import { RowContext } from '../row-context';
import { DisplayGridCheckboxCell } from '../display-grid-checkbox-cell';
import { DisplayGridHeaderCell } from './cell';

import style from './index.module.less';
import columnStyle from '../display-grid-column-group.module.less';

export const DisplayGridHeader = React.memo(({ columns, pinned = false }) => {
  useContextTypes('DisplayGridHeader', Context, {
    columns: PropTypes.arrayOf(DisplayGridColumnPropType).isRequired,
    data: PropTypes.arrayOf(PropTypes.oneOfType([DisplayGridRecordPropType, DisplayGridGroupRecordPropType])).isRequired,
    disableMultiSelect: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    getColumnId: PropTypes.func.isRequired,
    onColumnsChanged: PropTypes.func,
    onExpandedRowsChanged: PropTypes.func,
    onOrderByChanged: PropTypes.func,
    ref: PropTypes.shape({
      current: PropTypes.instanceOf(global.HTMLElement),
    }),
    activeGroupByColumns: PropTypes.array.isRequired,
    expandedGroupRows: PropTypes.instanceOf(Set),
  });

  const {
    columns: allColumns,
    data,
    dispatch,
    disableMultiSelect,
    renderCheckbox,
    getColumnId,
    onColumnsChanged,
    onOrderByChanged,
    ref,
    activeGroupByColumns,
  } = useContext(Context);

  const {
    expanded,
    onExpandedRowsChanged,
    selected,
    updateSelected,
    selectedGroupRows,
    setSelectedGroupRows,
    expandedGroupRows,
    setExpandedGroupRows,
  } = useContext(RowContext);

  const elementId = useMemo(() => `display-grid-${GuidService.newGuid()}`, []);
  const firstItem = data[0];
  const hasGroupData = firstItem[0]?.groupById ?? false;
  const totalDataCountWithGroups = hasGroupData ? data.reduce((acc, groupData) => acc + groupData.length - 1, data.length) : data.length;
  /**
   * Because the DisplayGrid is now using 'position: sticky' for its header row,
   * a due to a shortcoming in current browser implementations, when we start a
   * drag and drop operation to rearrange column headers in the sticky header
   * row, if the grid is scrolled down at all, the drag and drop operation will
   * be read from the sticky position (in this case, 'top: 0'), and will react
   * by attempting to scroll the grid upward to that position. To combat this,
   * for now at least, we have to continuously reset the scrollTop position on
   * the grid to whatever it is at the start of the drag operation. This reset
   * happens onScroll, and the act of constantly scrolling and resetting every
   * couple of pixels causes a very janky UI experience, but eventually, this
   * should be fixed by the browser vendors, and this can be removed.
   */
  const onBeforeDragStart = useCallback(() => {
    dispatch(setResetToScrollTop(ref.current.scrollTop));
  }, [dispatch, ref]);

  /**
   * This is called on each drag operation, to resize the columns in state, so
   * that we can offer a realtime view of resizing entire columns. We update
   * the state here on each incoming drag event, and only onResizeComplete do
   * we broadcast that outwardly to any consumers in the form of new columns.
   * We can call setIsResizing constantly, because it won't trigger a state
   * update at the reducer unless the value is actually different.
   */
  const onColumnResize = useCallback(
    (updatedColumn) => {
      const newColumns = allColumns.map((column) => {
        if (getColumnId(column) === getColumnId(updatedColumn)) {
          return updatedColumn;
        }

        return column;
      });
      dispatch(setColumns(newColumns));
    },
    [allColumns, getColumnId, dispatch],
  );

  const onColumnSortChange = useCallback(
    (column) => {
      if (onOrderByChanged) {
        const updatedSort = GridHelperService.getNextColumnSortOrder(column.sorted, allColumns);

        const newColumns = allColumns.map((col) => {
          if (col.name === column.name) {
            return {
              ...col,
              sorted: updatedSort,
            };
          }

          if (col.sorted) {
            const newCol = { ...col };

            delete newCol.sorted;

            return newCol;
          }

          return col;
        });

        onOrderByChanged(newColumns);
      }
    },
    [allColumns, onOrderByChanged],
  );

  /**
   * Once the drag operation finishes, we can reset the scrollTop to null,
   * then recalculate the final column order, and notify consumers via the
   * onColumnsChanged callback, if one has been supplied.
   */
  const onDragEnd = useCallback(
    (result) => {
      dispatch(setResetToScrollTop(null));

      // dropped outside the list
      if (!result.destination) {
        return;
      }

      const newColumnState = reorderGridColumns(allColumns, result.source.index, result.destination.index);

      if (onColumnsChanged) {
        onColumnsChanged(newColumnState);
      }
    },
    [allColumns, onColumnsChanged, dispatch],
  );

  /**
   * The header checkbox is responsible for checking/unchecking all records
   * in the current page of the DisplayGrid. If any of the records are checked,
   * the checkbox will be in an indeterminate state, and the next click should
   * uncheck all currently-checked records. If no records are checked, this
   * checkbox should be unchecked, and the next click should check all available
   * records on the current page. If all records are checked, this checkbox
   * should also be checked, and the next click should uncheck all records.
   */
  const onHeaderCheckboxChange = useCallback(
    (checked) => {
      const numberOfSelectedRows = selected.size;
      const numberOfSelectedGroups = selectedGroupRows.size;
      const allRowsSelected = numberOfSelectedRows === data.length || (data.length > 0 && numberOfSelectedGroups === data.length);
      const someRowsSelected = !allRowsSelected && numberOfSelectedRows + numberOfSelectedGroups !== 0;
      const isSelected = !someRowsSelected && checked;

      const newSelectedGroupSet = new Set();
      const newSelected = [];
      if (!isSelected) {
        updateSelected(emptyArray);
        setSelectedGroupRows(newSelectedGroupSet);
      } else {
        data.forEach((datum) => {
          const primaryRow = GridHelperService.getPrimaryRow(datum);
          if (primaryRow.groupById) {
            datum.forEach((innerGroupData) => {
              const innerPrimaryRow = GridHelperService.getPrimaryRow(innerGroupData);

              if (innerPrimaryRow.groupById) {
                newSelectedGroupSet.add(innerPrimaryRow.groupById);
              } else {
                newSelected.push(innerPrimaryRow.id);
              }
            });
          } else {
            newSelected.push(primaryRow.id);
          }
        });

        updateSelected(newSelected);
        setSelectedGroupRows(newSelectedGroupSet);
      }
    },
    [data, selected, selectedGroupRows, setSelectedGroupRows, updateSelected],
  );

  /**
   * This is called upon completion of a drag and drop operation that's currently
   * resizing column widths. The width recalculation is done per drag event and
   * stored to state in the reducer via the 'setColumns' action. Because this is
   * handled in another phase of this workflow, by the time the drag ends, we
   * already have the final calculated column widths stored in state. All we have
   * to do in this callback is clear our 'isResizing' bit, and broadcast the last
   * stores 'columns' value out to the 'onColumnsChanged' callback, if provided.
   */
  const onResizeComplete = useCallback(() => {
    dispatch(setIsResizing(false));

    if (onColumnsChanged) {
      onColumnsChanged(allColumns);
    }
  }, [allColumns, onColumnsChanged, dispatch]);

  // Calculate whether expander button should show in its expanded form or not.
  // This is calculated based on whether *all* expandable rows are expanded.
  const isExpanded =
    (expanded.size > 0 || expandedGroupRows.size > 0) &&
    data.reduce((allExpanded, datum) => {
      if (hasGroupData) {
        return datum.reduce((allExpandedInGroup, groupItem) => {
          if (groupItem.groupById) {
            return expandedGroupRows.has(groupItem.groupById);
          }
          if (Array.isArray(groupItem) && groupItem.length > 1) {
            const primaryRow = GridHelperService.getPrimaryRow(groupItem);
            return allExpandedInGroup && expanded.has(primaryRow.id);
          }
          return allExpandedInGroup;
        }, true);
      }
      if (Array.isArray(datum) && datum.length > 1) {
        const primaryRow = GridHelperService.getPrimaryRow(datum);
        return allExpanded && expanded.has(primaryRow.id);
      }

      return allExpanded;
    }, true);

  /**
   * This callback is fired when the expander button is clicked, and behaves
   * differently based on the value of 'isExpanded'. If everything is already
   * expanded, we collapse it all, otherwise, we expand it all, and only if
   * the consumer has provided an onExpandedRowsChanged callback. If we're
   * collapsing everything, use 'emptyObject' for the safety of a singleton.
   */
  const onToggleAllRows = useCallback(() => {
    if (onExpandedRowsChanged) {
      const newExpandedGroupSet = new Set();
      // If arrow is pointing downwards, collapse everything
      if (isExpanded) {
        onExpandedRowsChanged(new Set());
        setExpandedGroupRows(newExpandedGroupSet);
        return;
      }

      // Otherwise, expand everything that's expandable
      const newExpanded = data.reduce((rows, datum) => {
        if (hasGroupData) {
          datum.forEach((groupItem) => {
            if (groupItem.groupById) {
              newExpandedGroupSet.add(groupItem.groupById);
            }
            if (Array.isArray(groupItem)) {
              const primaryRow = GridHelperService.getPrimaryRow(groupItem);
              rows.add(primaryRow.id);
            }
          });
          return rows;
        }
        if (Array.isArray(datum)) {
          const primaryRow = GridHelperService.getPrimaryRow(datum);
          rows.add(primaryRow.id);
        }

        return rows;
      }, new Set());

      onExpandedRowsChanged(newExpanded);
      setExpandedGroupRows(newExpandedGroupSet);
    }
  }, [data, isExpanded, onExpandedRowsChanged, setExpandedGroupRows, hasGroupData]);

  // These cells don't change very often on any given render cycle. We can
  // use the useMemo hook to keep from rerendering them each time.
  const cells = useMemo(
    () =>
      columns.map((column, index) => (
        <DisplayGridHeaderCell
          key={getColumnId(column)}
          column={column}
          elementId={elementId}
          index={index}
          isActiveGroupBy={activeGroupByColumns.includes(getColumnId(column))}
          isDraggable={!pinned}
          onColumnResize={onColumnResize}
          onColumnSortChange={onColumnSortChange}
          onResizeComplete={onResizeComplete}
        />
      )),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [columns, getColumnId, pinned, activeGroupByColumns],
  );

  // Same thing here. These cells won't have their state altered on a regular
  // basis during the render cycle, so memoize the render result for posterity.
  const renderedChildren = useMemo(() => {
    const wrapperClassNames = classnames(style.flex, {
      [style['has-active-groupbys']]: activeGroupByColumns.length,
    });
    if (!pinned) {
      return (
        <DragDropContext onBeforeDragStart={onBeforeDragStart} onDragEnd={onDragEnd}>
          <Droppable direction='horizontal' droppableId='droppable'>
            {(provided) => (
              <div ref={provided.innerRef} className={wrapperClassNames}>
                {cells}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      );
    }

    const numberSelected = selected.size + selectedGroupRows.size;
    const showExpander = !!data.find((datum) => GridHelperService.getGridRowCount(datum, true) > 1);
    const indeterminate = numberSelected !== 0 && numberSelected !== totalDataCountWithGroups;

    return (
      <div className={wrapperClassNames}>
        <DisplayGridCheckboxCell
          checked={data.length > 0 && numberSelected === totalDataCountWithGroups}
          className={style.checkbox}
          disabled={data.length === 0 || disableMultiSelect}
          indeterminate={indeterminate}
          isExpanded={isExpanded}
          onChange={onHeaderCheckboxChange}
          renderCheckbox={renderCheckbox}
          shouldRenderToggler={showExpander}
          toggleRow={onToggleAllRows}
        />
        {cells}
      </div>
    );
  }, [
    cells,
    data,
    disableMultiSelect,
    renderCheckbox,
    isExpanded,
    onBeforeDragStart,
    onDragEnd,
    onHeaderCheckboxChange,
    onToggleAllRows,
    pinned,
    selected,
    activeGroupByColumns.length,
    selectedGroupRows.size,
    totalDataCountWithGroups,
  ]);

  const classNames = classnames(style['header-row'], {
    [columnStyle['pinned-columns']]: pinned,
    [columnStyle['non-pinned-columns']]: !pinned,
  });

  return <div className={classNames}>{renderedChildren}</div>;
});

DisplayGridHeader.propTypes = {
  columns: PropTypes.arrayOf(DisplayGridColumnPropType).isRequired,
  pinned: PropTypes.bool,
};
